import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { setCurrentStep, setAppointmentSelection } from "../../checkoutSlice";
import { selectAppointmentSelection } from "../../checkoutSelectors";
import { useGetCartQuery } from "../../../cart/api/cartApi";
import { useLazyGetAvailableTimesQuery } from "../../api/appointmentApi";
import { useUpdateCustomer } from "../../../cart/hooks/useUpdateCustomer";
import CheckoutStepLayout from "../common/CheckoutStepLayout";
import Icon from "../../../../../common/components/Icon/Icon";

const AppointmentStep: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Navigation conditions
  const { data: cartData } = useGetCartQuery();
  const needsShipping = cartData?.needsShipping ?? true;

  // Redux state
  const { date: savedDate, time: savedTime } = useAppSelector(
    selectAppointmentSelection,
  );

  // Local state
  const [selectedDate, setSelectedDate] = useState<string>(savedDate);
  const [selectedTime, setSelectedTime] = useState<string>(savedTime);

  // API Hooks
  const [fetchTimes, { data: availableTimes, isFetching, isError }] =
    useLazyGetAvailableTimesQuery();
  const { handleUpdateCustomer, isUpdatingCustomer } = useUpdateCustomer();

  // Re-hydrate local state if Redux state updates (e.g., after a refresh finishes loading)
  useEffect(() => {
    if (savedDate && !selectedDate) setSelectedDate(savedDate);
    if (savedTime && !selectedTime) setSelectedTime(savedTime);
  }, [savedDate, savedTime, selectedDate, selectedTime]);

  const today = new Date();
  // Adjust for local timezone offset to prevent picking yesterday's date if UTC is behind
  const tzOffset = today.getTimezoneOffset() * 60000;
  const localISOTime = new Date(Date.now() - tzOffset)
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    if (selectedDate) {
      fetchTimes(selectedDate);
    }
  }, [selectedDate, fetchTimes]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    // Validate Sunday Selection (0 is Sunday)
    const dayOfWeek = new Date(val).getDay();
    if (dayOfWeek === 0) {
      toast.error(
        t(
          "checkoutPage.appointment.sundayError",
          "Pazar günleri randevu alamıyoruz. Lütfen başka bir gün seçin.",
        ),
      );
      setSelectedDate("");
      setSelectedTime("");
      return;
    }

    setSelectedDate(val);
    setSelectedTime(""); // Always reset time when date changes
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      toast.error(
        t(
          "checkoutPage.appointment.missingError",
          "Lütfen bir tarih ve saat seçin.",
        ),
      );
      return;
    }

    // 1. Sync data to the server session via the Cart Update endpoint
    const result = await handleUpdateCustomer({
      extensions: {
        appointment_data: {
          date: selectedDate,
          time: selectedTime,
        },
      },
    });

    if (result.success) {
      // 2. Save to Redux and route to Payment Step
      dispatch(
        setAppointmentSelection({ date: selectedDate, time: selectedTime }),
      );
      dispatch(setCurrentStep("payment"));
    }
  };

  const handlePreviousStep = () => {
    // Dynamic backward routing
    const prevStep = needsShipping ? "shipping" : "billing";
    dispatch(setCurrentStep(prevStep));
  };

  return (
    <CheckoutStepLayout
      title={t("checkoutPage.appointment.stepTitle", "Randevu Seçimi")}
      onSubmit={onSubmit}
      isProcessing={isUpdatingCustomer} // Show loader button while syncing to server
      nextButtonText={t("checkoutForm.actions.continue", "Continue")}
      isNextDisabled={!selectedDate || !selectedTime}
      showPrevButton={true}
      prevButtonType="button"
      onPrevious={handlePreviousStep}
    >
      <div className="checkout-form__section">
        <p
          style={{
            marginBottom: "1.5rem",
            color: "var(--color-text-secondary)",
          }}
        >
          {t(
            "checkoutPage.appointment.description",
            "Sepetinizdeki ürün/hizmetler için uygun bir randevu tarihi ve saati belirleyiniz.",
          )}
        </p>

        <div className="form-field">
          <label htmlFor="appointment-date" className="required">
            {t("checkoutPage.appointment.dateLabel", "Tarih Seçiniz")}
          </label>
          <input
            type="date"
            id="appointment-date"
            value={selectedDate}
            min={localISOTime}
            onChange={handleDateChange}
            className="form-field__input"
            disabled={isUpdatingCustomer}
          />
        </div>

        {selectedDate && (
          <div className="form-field" style={{ marginTop: "1.5rem" }}>
            <label className="required">
              {t("checkoutPage.appointment.timeLabel", "Saat Seçiniz")}
            </label>

            {isFetching ? (
              <div
                className="loader-spinner"
                style={{ marginTop: "10px" }}
              ></div>
            ) : isError ? (
              <p className="form-field__error">
                Saatler yüklenirken hata oluştu.
              </p>
            ) : availableTimes && availableTimes.length > 0 ? (
              <div className="appointment-times-grid">
                {availableTimes.map((time) => (
                  <button
                    key={time}
                    type="button"
                    disabled={isUpdatingCustomer}
                    className={`appointment-time-btn ${selectedTime === time ? "is-active" : ""}`}
                    onClick={() => setSelectedTime(time)}
                  >
                    <Icon iconName="clock" />
                    <span>{time}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p style={{ color: "var(--color-danger)", marginTop: "10px" }}>
                {t(
                  "checkoutPage.appointment.fullyBooked",
                  "Seçtiğiniz tarihte uygun saat bulunmamaktadır. Lütfen başka bir gün seçin.",
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </CheckoutStepLayout>
  );
};

export default AppointmentStep;
